import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';

import { AccountContact, AccountContactDTO, IContactPermissions, IApiResult, MarketDataPermissions } from 'common';
import { map, switchMap } from 'rxjs/operators';

import { AppConfigService, SecurityService, Market } from 'common';
import { Observable, Subject, of } from 'rxjs';
import { ApplicationPermissions } from '../models/application-permissions.model';

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private applicationId: string;
  private permissionsSubject = new Subject<number>();
  permissionChanges$ = this.permissionsSubject.asObservable();

  validationTriggered = new EventEmitter<number[]>();

  constructor(
    private security: SecurityService,
    private httpClient: HttpClient,
    env: AppConfigService
  ) {
    this.applicationId = env.config.applicationId;
  }

  async getContacts(): Promise<AccountContact[]> {
    return await this.httpClient
      .get<IApiResult<AccountContactDTO[]>>('/v2/Accounts/:accountCode/contacts?includeContactsWithNoEmail=true')
      .pipe(map((response) => response.value.map((item) => AccountContact.fromDto(item))))
      .toPromise();
  }

  getProviderType(): number {
    return this.security.profile.selectedAccount?.providerType;
  }

  getCurrentUsername(): string {
    return this.security.profile.username;
  }

  getProviderTypeString(): string {
    return this.security.profile.selectedAccount?.providerTypeString;
  }

  removeContact(lcAuto: number, transferLcAuto?: number): Observable<number[]> {
    return this.httpClient
      .delete<IApiResult<number[]>>(`/v2/Accounts/:accountCode/contacts/${lcAuto}/applications/${this.applicationId}/${transferLcAuto ?? ''}`)
      .pipe(map((response) => response.value));
  }

  addContact(contact: AccountContact): Observable<boolean> {
    return this.validatePermissions(contact, this.applicationId).pipe(
      switchMap((validationResults: number[]) => {
        if (validationResults.length == 0) {
          const dto = contact.convertToDto();

          return this.httpClient
            .post<IApiResult<boolean>>(`/v2/Accounts/:accountCode/types/${this.security.profile.selectedAccount?.providerTypeString}/contacts`, dto)
            .pipe(
              map((response) => {
                return !(response.errors && response.errors.length > 0);
              })
            );
        } else {
          this.validationTriggered.emit(validationResults);
          return of(false);
        }
      })
    );
  }

  updateContact(contact: AccountContact): Observable<boolean> {
    return this.validatePermissions(contact, this.applicationId).pipe(
      switchMap((validationResults: number[]) => {
        if (validationResults.length == 0) {
          const dto = contact.convertToDto();

          return this.httpClient
            .put<
              IApiResult<boolean>
            >(`/v2/Accounts/:accountCode/types/${this.security.profile.selectedAccount?.providerTypeString}/contacts/${dto.lcAuto}`, dto)
            .pipe(
              map((response) => {
                return !(response.errors && response.errors.length > 0);
              })
            );
        } else {
          this.validationTriggered.emit(validationResults);
          return of(false);
        }
      })
    );
  }

  async getContact(lcAuto: string): Promise<AccountContact> {
    return await this.httpClient
      .get<IApiResult<AccountContactDTO>>(`/v2/Accounts/:accountCode/contacts/${lcAuto}`)
      .pipe(map((response) => AccountContact.fromDto(response.value)))
      .toPromise();
  }

  async getApplicationPermissions(application: string): Promise<ApplicationPermissions> {
    return await this.httpClient
      .get<IApiResult<ApplicationPermissions>>(`/lookups/applications/${application}/permissions`)
      .pipe(map((response) => response.value))
      .toPromise();
  }

  changePermission(newPermissionSet: number) {
    this.permissionsSubject.next(newPermissionSet);
  }

  validatePermissions(contactPermissions: IContactPermissions, application: string): Observable<number[]> {
    return this.httpClient
      .get<
        IApiResult<number[]>
      >(`/users/${contactPermissions.email}/accounts/:accountCode/applications/${application}/permissions/${contactPermissions.accountPermissions}/validate`)
      .pipe(map((response) => response.value));
  }

  getMarketsAvailableByAccountCode(showAll: boolean = false): Observable<Market[]> {
    const apiUrl = `/MarketData/accounts/:accountCode/markets?showAll=${showAll}`;
    return this.httpClient.get<Market[]>(apiUrl);
  }

  public mapMarketIdToPermission(marketId: number): MarketDataPermissions {
    switch (marketId) {
      case 1:
        return MarketDataPermissions.FixedBroadband;
      case 2:
        return MarketDataPermissions.LeasedLines;
      case 3:
        return MarketDataPermissions.NetworkMaps;
      case 4:
        return MarketDataPermissions.SpacialData;
      case 5:
        return MarketDataPermissions.Revenue;
      case 6:
        return MarketDataPermissions.Offers;
      case 7:
        return MarketDataPermissions.Investment;
      case 8:
        return MarketDataPermissions.Mobile;
      case 9:
        return MarketDataPermissions.Complaints;
      case 10:
        return MarketDataPermissions.PIA;
      case 11:
        return MarketDataPermissions.ScamMetrics;
    }
  }
}
